import { memo } from "react";

import type { BillingTypeTerm } from "@/lib/gql/__generated__/graphql";

import { FeatureIslandCategoryQuestionForMain } from "@/components/features/_layouts/island/main-with-category/components/category-main-question";
import type { ForNodeType } from "@/components/features/_model/types";
import type { ForAutoGenerator } from "@/components/features/_model/types/auto-generator-for-view-model";

import type { FormOptionType } from "@/components/ui/form/_types/type";
import { InputNumberField } from "@/components/ui/form/fields/input-field/input-number";
import { SelectField } from "@/components/ui/form/fields/select-field";

import { QuestionSectionWrapper } from "../../../../../_shared/ui/components/system/question/_layouts/item-wrapper";

import type { PriceObjectZodSchemaType } from "../../../../../../../_shared/validation-schema/validation-schema";

type PriceObjectType = ForAutoGenerator<PriceObjectZodSchemaType, "">;

type Props = {
	price: PriceObjectType;
	onSetPriceAmount: (input: number | undefined) => void;

	billingTypeTerm: ForNodeType<BillingTypeTerm>;
	onSetBillingTypeTerm: (input: string) => void;
	billingTypeTermOptions: FormOptionType[];
	shouldShowBillingTypeTerm: boolean;

	isEdited: boolean;
	hasError: boolean;

	title?: string;
	guide?: string;
	isPriceDisabled?: boolean;
};

export const QuestionOptionPrice = memo(
	({
		isEdited,
		hasError,

		price,
		onSetPriceAmount,

		billingTypeTerm,
		onSetBillingTypeTerm,
		billingTypeTermOptions,
		shouldShowBillingTypeTerm,

		title: _title,
		guide: _guide,
		isPriceDisabled,
	}: Props) => {
		const title = _title ?? "オプションの料金";
		const guide =
			_guide ??
			"「1泊ごとに」を選択した場合、日ごとにオプションを販売できます。<br />「宿泊を通して」を選択した場合、宿泊全体を通して販売され、宿泊日数分の在庫が消費されます。";

		return (
			<FeatureIslandCategoryQuestionForMain
				required={true}
				title={title}
				guide={guide}
				isEdited={isEdited}
				hasError={hasError}
				inputChildren={
					<>
						{isPriceDisabled && (
							<div className="text-sm">
								※
								現在、サブオプションで料金の設定がされています。そのため、全体での料金設定を行う必要はありません。
							</div>
						)}
						<QuestionSectionWrapper>
							{shouldShowBillingTypeTerm && (
								<SelectField
									value={billingTypeTerm.value}
									options={billingTypeTermOptions}
									errorMessage={billingTypeTerm.error}
									handleValueChange={onSetBillingTypeTerm}
								/>
							)}

							<div className="flex flex-col gap-y-1">
								<p>１個あたり</p>
								<InputNumberField
									value={price.amount?.value}
									handleInput={onSetPriceAmount}
									errorMessage={price.amount?.error}
									disabled={isPriceDisabled}
								/>
								<p className="text-right">円（税込）</p>
							</div>
						</QuestionSectionWrapper>
					</>
				}
			/>
		);
	},
);

QuestionOptionPrice.displayName = "QuestionOptionPrice";
